import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";
import ItemPicker from "./ItemPicker";
import SuggestedPricing from "./SuggestedPricing";

const ACCENT = "#fcd657";
const PURPLE = "#ce93d8";

// ─── Lazy Image ──────────────────────────────────────────────────────────────
function LazyImage({ src, alt, style, thumbnailWidth }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const isGif = src && (src.toLowerCase().endsWith(".gif") || src.toLowerCase().includes(".gif?"));
  const optimizedSrc = src && thumbnailWidth && !isGif && src.includes("/storage/v1/object/") ? `${src}?width=${thumbnailWidth}&resize=contain` : src;
  return (
    <div ref={imgRef} style={{ ...style, position: "relative", overflow: "hidden" }}>
      {!loaded && <div style={{ position: "absolute", inset: 0, background: `${ACCENT}06`, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 28, color: `${ACCENT}18` }}>◇</div></div>}
      {inView && <img src={optimizedSrc} alt={alt} onLoad={() => setLoaded(true)} onError={() => setLoaded(true)} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: loaded ? 1 : 0, transition: "opacity 0.35s ease" }} />}
    </div>
  );
}

// ─── Star Field ──────────────────────────────────────────────────────────────
function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d"); let animId;
    const stars = Array.from({ length: 200 }, () => ({ x: Math.random(), y: Math.random(), r: Math.random() * 1.5 + 0.3, a: Math.random(), speed: Math.random() * 0.003 + 0.001 }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; }; resize(); window.addEventListener("resize", resize);
    const draw = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); stars.forEach(s => { s.a += s.speed; if (s.a > 1) s.a = 0; const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.1; ctx.beginPath(); ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(255,255,255,${alpha})`; ctx.fill(); }); animId = requestAnimationFrame(draw); }; draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

function SectionLabel({ children, color = ACCENT }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <div style={{ width: 32, height: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

// ─── Catalog Picker Row ──────────────────────────────────────────────────────
// Reused by every listing-creation form (marketplace, bounty, admin store).
// Shows a "Pick from SC Items catalog" button; when linked, displays a chip
// with the catalog item's name + wiki link + an Unlink button. Picking is
// pure convenience — the seller can still edit any field afterward.
function CatalogPickerRow({ form, setForm, pickerOpen, setPickerOpen, applyCatalogPick, accent = ACCENT }) {
  const linked = !!form.sc_item_id;
  return (
    <div>
      <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${accent}88`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
        Catalog Link (Optional)
      </label>
      {linked ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${accent}44`, background: `${accent}08` }}>
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, color: accent, textTransform: "uppercase" }}>SC Item</span>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.title || "(no title)"}</span>
          {form.sc_item_source_url && (
            <a href={form.sc_item_source_url} target="_blank" rel="noreferrer"
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: accent, textTransform: "uppercase", textDecoration: "none" }}>
              Wiki ↗
            </a>
          )}
          <button type="button" onClick={() => setForm(f => ({ ...f, sc_item_id: null, sc_item_source_url: "" }))}
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.5)", textTransform: "uppercase", cursor: "pointer" }}>
            Unlink
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setPickerOpen(true)}
          style={{ width: "100%", padding: "10px 12px", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.8, textAlign: "left", background: "transparent", border: `1px dashed ${accent}55`, color: `${accent}cc`, textTransform: "uppercase", cursor: "pointer" }}>
          + Pick from SC Items Catalog
        </button>
      )}
      <ItemPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={applyCatalogPick} accentColor={accent} title="Link to Catalog Item" />
    </div>
  );
}

// ─── Price Badge ─────────────────────────────────────────────────────────────
function PriceBadge({ listing }) {
  const prices = [];
  if (listing.accepts_merits && listing.merit_price != null) prices.push({ label: `${listing.merit_price} Merits`, color: "#fcd657" });
  if (listing.accepts_auec && listing.auec_price != null) {
    // listing._peggedAuec is stamped by MarketPage.loadData when pricing_mode='merit_linked'.
    const label = listing._peggedAuec
      ? `${Number(listing.auec_price).toLocaleString()} aUEC · PEGGED`
      : `${Number(listing.auec_price).toLocaleString()} aUEC`;
    prices.push({ label, color: "#4fc3f7" });
  }
  if (listing.accepts_cash && listing.cash_price != null) prices.push({ label: `$${Number(listing.cash_price).toFixed(2)}`, color: "#34d399" });
  if (prices.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {prices.map((p, i) => (
        <span key={i} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: p.color, background: `${p.color}15`, padding: "3px 10px", border: `1px solid ${p.color}44` }}>{p.label}</span>
      ))}
    </div>
  );
}

// ─── Status Badge ────────────────────────────────────────────────────────────
function StatusBadge({ listing }) {
  if (listing.status === "sold_out") return <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, color: "#ef5350", background: "rgba(239,83,80,0.15)", padding: "4px 12px", border: "1px solid rgba(239,83,80,0.4)" }}>SOLD OUT</span>;
  if (!listing.is_permanent && listing.quantity != null) {
    const remaining = listing.quantity - listing.quantity_sold;
    return <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: remaining <= 3 ? "#ef5350" : "rgba(232,234,246,0.5)", letterSpacing: 1 }}>{remaining} left</span>;
  }
  return null;
}

// ─── Listing Card (Fleet-style) ──────────────────────────────────────────────
function ListingCard({ listing, onClick, sellerName, storeTag }) {
  const TAG_COLORS = { merit: "#fcd657", marketplace: "#4fc3f7", swag: "#34d399", bounty: "#ce93d8" };
  const TAG_LABELS = { merit: "MERIT", marketplace: "MARKET", swag: "SWAG", bounty: "BOUNTY" };
  const isSoldOut = listing.status === "sold_out";
  return (
    <div onClick={isSoldOut ? undefined : onClick} style={{
      border: `1px solid ${isSoldOut ? "rgba(255,255,255,0.06)" : `${ACCENT}25`}`,
      background: isSoldOut ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.02)",
      cursor: isSoldOut ? "default" : "pointer", transition: "all 0.25s", overflow: "hidden",
      clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)",
      opacity: isSoldOut ? 0.5 : 1, position: "relative",
      filter: isSoldOut ? "grayscale(0.6)" : "none",
    }}
      onMouseEnter={e => { if (!isSoldOut) { e.currentTarget.style.borderColor = `${ACCENT}55`; e.currentTarget.style.background = `${ACCENT}08`; e.currentTarget.style.transform = "translateY(-2px)"; } }}
      onMouseLeave={e => { if (!isSoldOut) { e.currentTarget.style.borderColor = `${ACCENT}25`; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.transform = "translateY(0)"; } }}>
      {/* Image */}
      <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "rgba(0,0,0,0.3)" }}>
        {listing.image_url ? (
          <LazyImage src={listing.image_url} alt={listing.title} thumbnailWidth={480} style={{ width: "100%", height: "100%" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 36, color: `${ACCENT}12` }}>◇</div>
        )}
        {/* Sold out banner */}
        {isSoldOut && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(5,7,15,0.7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 900, letterSpacing: 4, color: "#ef5350", textTransform: "uppercase", transform: "rotate(-12deg)", textShadow: "0 0 20px rgba(239,83,80,0.4)" }}>SOLD OUT</div>
          </div>
        )}
        {/* Quantity badge */}
        {!isSoldOut && !listing.is_permanent && listing.quantity != null && (
          <div className="listing-badge" style={{ position: "absolute", top: 8, right: 8, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: `1px solid ${ACCENT}44` }}>
            <span className="listing-badge-num" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: ACCENT }}>{listing.quantity - listing.quantity_sold}</span>
            <span className="listing-badge-label-hide-mobile" style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>LEFT</span>
          </div>
        )}
        {!isSoldOut && listing.is_permanent && (
          <div className="listing-badge" style={{ position: "absolute", top: 8, right: 8, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: `1px solid ${ACCENT}44` }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: `${ACCENT}99`, letterSpacing: 1 }}><span className="listing-badge-label-hide-mobile">ALWAYS </span>AVAILABLE</span>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="listing-card-content" style={{ padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <div className="listing-title" style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: "#e8eaf6", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, marginBottom: 0 }}>{listing.title}</div>
          {listing.sc_item_id && (
            <span title={listing._sc_category || listing._sc_group || "SC Items catalog"}
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1.5, padding: "2px 6px", border: `1px solid ${ACCENT}55`, color: ACCENT, flexShrink: 0 }}>
              CATALOG
            </span>
          )}
        </div>
        {listing.description && (
          <div className="listing-desc" style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.4)", marginBottom: 8, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{listing.description}</div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <PriceBadge listing={listing} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {storeTag && <span className="listing-store-tag" style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1.5, padding: "2px 6px", border: `1px solid ${TAG_COLORS[storeTag] || ACCENT}44`, color: TAG_COLORS[storeTag] || ACCENT, flexShrink: 0 }}>{TAG_LABELS[storeTag] || storeTag.toUpperCase()}</span>}
            {sellerName && <span className="listing-seller listing-meta" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>by {sellerName}</span>}
          </div>
        </div>
        {/* Auction info on card */}
        {listing.is_auction && listing.auction_end && (
          <div className="listing-auction" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, padding: "6px 10px", background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.15)", gap: 8, flexWrap: "wrap" }}>
            <span className="listing-auction-label" style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "#a78bfa" }}>AUCTION</span>
            <AuctionCountdown endTime={listing.auction_end} />
            {listing.current_bid != null && (
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, color: listing.bid_currency === "merits" ? "#fcd657" : "#4fc3f7" }}>
                {listing.bid_currency === "merits" ? `${listing.current_bid} M` : `${Number(listing.current_bid).toLocaleString()}`}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Cart Sidebar ────────────────────────────────────────────────────────────
function CartSidebar({ cartItems, listings, profiles, onClose, onRemoveItem, onCheckout, checkingOut, meritBalance }) {
  const grouped = useMemo(() => {
    // Group cart items by seller for marketplace items
    const groups = {};
    cartItems.forEach(ci => {
      const listing = listings.find(l => l.id === ci.listing_id);
      if (!listing) return;
      const key = listing.store_type === "marketplace" ? (listing.listed_by || "unknown") : listing.store_type;
      if (!groups[key]) groups[key] = { sellerId: listing.store_type === "marketplace" ? listing.listed_by : null, storeType: listing.store_type, items: [] };
      groups[key].items.push({ ...ci, listing });
    });
    return Object.values(groups);
  }, [cartItems, listings]);

  const totalMerits = cartItems.reduce((sum, ci) => {
    const listing = listings.find(l => l.id === ci.listing_id);
    if (ci.payment_type === "merits" && listing) return sum + (listing.merit_price || 0) * ci.quantity;
    return sum;
  }, 0);

  const totalAuec = cartItems.reduce((sum, ci) => {
    const listing = listings.find(l => l.id === ci.listing_id);
    if (ci.payment_type === "auec" && listing) return sum + (listing.auec_price || 0) * ci.quantity;
    return sum;
  }, 0);

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 90vw)", zIndex: 1100, background: "rgba(5,7,15,0.98)", backdropFilter: "blur(20px)", borderLeft: `1px solid ${ACCENT}33`, display: "flex", flexDirection: "column", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ padding: "24px 24px 16px", borderBottom: `1px solid ${ACCENT}22`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <SectionLabel color={ACCENT}>Your Cart</SectionLabel>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: "#e8eaf6" }}>{cartItems.length} Item{cartItems.length !== 1 ? "s" : ""}</div>
        </div>
        <button onClick={onClose} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, color: "rgba(232,234,246,0.5)", background: "none", border: "none", cursor: "pointer", padding: 8 }}>✕</button>
      </div>

      {/* Cart items */}
      <div style={{ flex: 1, padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
        {cartItems.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>CART EMPTY</div>
        )}
        {grouped.map((group, gi) => {
          const sellerProfile = group.sellerId ? profiles.find(p => p.id === group.sellerId) : null;
          const groupLabel = group.storeType === "marketplace" ? (sellerProfile?.discord_username || "Unknown Seller") : group.storeType === "merit" ? "Merit Store" : "Swag Store";
          return (
            <div key={gi} style={{ border: `1px solid ${ACCENT}15`, background: `${ACCENT}03`, padding: 16 }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", marginBottom: 12 }}>{groupLabel}</div>
              {group.items.map(item => (
                <div key={item.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ width: 48, height: 48, flexShrink: 0, overflow: "hidden", background: "rgba(0,0,0,0.3)" }}>
                    {item.listing.image_url ? <img src={item.listing.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 14, color: `${ACCENT}20` }}>◇</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.listing.title}</div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)" }}>
                      {item.payment_type === "merits" && `${item.listing.merit_price} Merits`}
                      {item.payment_type === "auec" && `${Number(item.listing.auec_price).toLocaleString()} aUEC`}
                      {item.payment_type === "cash" && `$${Number(item.listing.cash_price).toFixed(2)}`}
                      {item.quantity > 1 && ` × ${item.quantity}`}
                    </div>
                  </div>
                  <button onClick={() => onRemoveItem(item.id)} style={{ fontFamily: "'Orbitron'", fontSize: 14, color: "rgba(239,83,80,0.6)", background: "none", border: "none", cursor: "pointer", padding: 4 }}>✕</button>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Footer / totals */}
      {cartItems.length > 0 && (
        <div style={{ padding: 24, borderTop: `1px solid ${ACCENT}22` }}>
          {totalMerits > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.6)" }}>Merits Total</span>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: "#fcd657" }}>{totalMerits}</span>
            </div>
          )}
          {totalMerits > 0 && meritBalance != null && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.35)" }}>Your Available Merits</span>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: meritBalance.available >= totalMerits ? "#34d399" : "#ef5350" }}>{meritBalance.available}</span>
            </div>
          )}
          {totalAuec > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.6)" }}>aUEC Total</span>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: "#4fc3f7" }}>{totalAuec.toLocaleString()}</span>
            </div>
          )}
          {totalMerits > 0 && meritBalance && meritBalance.available < totalMerits ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ef5350", letterSpacing: 1, textAlign: "center", padding: "12px 0" }}>INSUFFICIENT MERITS</div>
          ) : (
            <button onClick={onCheckout} disabled={checkingOut} style={{
              width: "100%", padding: "14px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase",
              background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: checkingOut ? "wait" : "pointer", transition: "all 0.2s",
            }}>{checkingOut ? "PROCESSING..." : "CHECKOUT"}</button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Create Listing Modal (for Marketplace tab) ──────────────────────────────
function CreateListingModal({ onClose, onCreated, profile, sections, meritPegAuec }) {
  const [form, setForm] = useState({
    title: "", description: "", image_url: "", section_id: "",
    accepts_merits: false, accepts_auec: true,
    merit_price: "", auec_price: "", is_permanent: false, quantity: "1",
    is_auction: false, bid_currency: "auec", starting_bid: "", auction_days: "3",
    // 'manual' = use auec_price as entered; 'merit_linked' = aUEC is derived
    // from merit_price × the org's Merit peg, so the listing tracks the peg.
    pricing_mode: "manual",
    // Optional link to an SC Items catalog row — convenience auto-fill only,
    // everything (including the photo) stays editable afterwards.
    sc_item_id: null,
    sc_item_source_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInputRef = React.useRef(null);

  function applyCatalogPick(item) {
    if (!item) return;
    setForm(f => ({
      ...f,
      title: item.name || f.title,
      description: item.description || f.description,
      image_url: item.image_url || f.image_url,
      sc_item_id: item.id,
      sc_item_source_url: item.source_url || "",
    }));
    setPickerOpen(false);
  }

  const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/avif"];

  async function uploadFile(file) {
    if (!file || !ACCEPTED_TYPES.includes(file.type)) return;
    setUploading(true);
    const ext = file.name.split(".").pop().toLowerCase();
    const path = `marketplace/${profile.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("store-media").upload(path, file, { contentType: file.type });
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from("store-media").getPublicUrl(path);
      setForm(f => ({ ...f, image_url: publicUrl }));
    }
    setUploading(false);
  }

  function handleImageUpload(e) { uploadFile(e.target.files?.[0]); }

  function handleDrop(e) {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && ACCEPTED_TYPES.includes(file.type)) uploadFile(file);
  }

  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragOver(true); }
  function handleDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); }

  async function handleSubmit() {
    if (!form.title.trim()) return;
    setSaving(true);
    const auctionEnd = form.is_auction ? new Date(Date.now() + (parseInt(form.auction_days) || 3) * 24 * 60 * 60 * 1000).toISOString() : null;
    // Merit-linked listings always accept both currencies — the aUEC side is
    // just a live derivation of the merit price × peg. Force-enable accepts_auec
    // and accepts_merits so buyers can pay either way.
    const isMeritLinked = !form.is_auction && form.pricing_mode === "merit_linked";
    const acceptsMerits = form.is_auction ? form.bid_currency === "merits" : (isMeritLinked || form.accepts_merits);
    const acceptsAuec   = form.is_auction ? form.bid_currency === "auec"   : (isMeritLinked || form.accepts_auec);
    // When merit-linked, persist the merit_price and let auec_price be derived
    // at read time (we still stamp a snapshot so legacy reads work).
    const meritPriceParsed = parseInt(form.merit_price) || null;
    const derivedAuec = isMeritLinked && meritPriceParsed && meritPegAuec ? Math.round(meritPriceParsed * meritPegAuec) : null;
    const payload = {
      store_type: "marketplace", section_id: form.section_id || null,
      title: form.title.trim(), description: form.description.trim(), image_url: form.image_url,
      sc_item_id: form.sc_item_id || null,
      accepts_merits: acceptsMerits,
      accepts_auec: acceptsAuec,
      accepts_cash: false,
      pricing_mode: isMeritLinked ? "merit_linked" : "manual",
      merit_price: !form.is_auction && acceptsMerits ? meritPriceParsed : null,
      auec_price: isMeritLinked ? derivedAuec : (!form.is_auction && acceptsAuec ? parseInt(form.auec_price) || null : null),
      is_permanent: form.is_auction ? false : form.is_permanent,
      quantity: form.is_auction ? 1 : (form.is_permanent ? null : parseInt(form.quantity) || 1),
      listed_by: profile.id,
      is_auction: form.is_auction,
      auction_end: auctionEnd,
      bid_currency: form.is_auction ? form.bid_currency : null,
      starting_bid: form.is_auction ? parseInt(form.starting_bid) || 0 : null,
      current_bid: null, current_bidder_id: null,
    };
    const { error } = await supabase.from("store_listings").insert([payload]);
    setSaving(false);
    if (!error) { onCreated(); onClose(); }
  }

  const inputStyle = { width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: "#0a0f1c", border: `1px solid ${ACCENT}44`, padding: 32, maxWidth: 520, width: "100%", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <SectionLabel>New Listing</SectionLabel>
        <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: "#e8eaf6", marginBottom: 24 }}>Sell an Item</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* SC Items catalog convenience picker — optional, auto-fills title/description/image */}
          <CatalogPickerRow form={form} setForm={setForm} pickerOpen={pickerOpen} setPickerOpen={setPickerOpen} applyCatalogPick={applyCatalogPick} />

          {form.sc_item_id && form.title && (
            <SuggestedPricing
              itemName={form.title}
              meritPegAuec={meritPegAuec}
              pricingMode={form.pricing_mode}
              onApply={(auec) => {
                if (form.pricing_mode === "merit_linked" && meritPegAuec) {
                  setForm(f => ({ ...f, merit_price: String(Math.max(1, Math.round(auec / meritPegAuec))) }));
                } else {
                  setForm(f => ({ ...f, accepts_auec: true, auec_price: String(auec) }));
                }
              }}
            />
          )}

          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Title</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="What are you selling?" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the item..." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          {sections.length > 0 && (
            <div>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Category</label>
              <select value={form.section_id} onChange={e => setForm(f => ({ ...f, section_id: e.target.value }))} style={{ ...inputStyle, background: "#0a0610" }}>
                <option value="">None</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Image</label>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/avif" onChange={handleImageUpload} style={{ display: "none" }} />
            {form.image_url ? (
              <div style={{ position: "relative", width: 120, height: 120, border: `1px solid ${ACCENT}33`, background: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={() => fileInputRef.current?.click()}>
                <img src={form.image_url} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#fff", letterSpacing: 1 }}>CHANGE</span>
                </div>
                <button onClick={e => { e.stopPropagation(); setForm(f => ({ ...f, image_url: "" })); }} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#ef5350", border: "none", color: "#fff", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>×</button>
              </div>
            ) : (
              <div
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? ACCENT : ACCENT + "44"}`, background: dragOver ? `${ACCENT}10` : "rgba(255,255,255,0.02)", padding: "28px 20px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
              >
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, color: `${ACCENT}${dragOver ? "" : "44"}`, marginBottom: 8 }}>⬡</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: dragOver ? ACCENT : "rgba(232,234,246,0.4)", marginBottom: 4 }}>
                  {uploading ? "Uploading..." : dragOver ? "Drop image here" : "Drag & drop an image here"}
                </div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", letterSpacing: 1 }}>
                  or click to browse · PNG, JPG, GIF, WebP
                </div>
              </div>
            )}
          </div>

          {/* Auction toggle */}
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 10 }}>Sale Type</label>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" checked={!form.is_auction} onChange={() => setForm(f => ({ ...f, is_auction: false }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6" }}>Fixed Price</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" checked={form.is_auction} onChange={() => setForm(f => ({ ...f, is_auction: true }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#a78bfa" }}>Auction / Bidding</span>
              </label>
            </div>
          </div>

          {form.is_auction ? (
            <>
              {/* Auction settings */}
              <div>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#a78bfa88", textTransform: "uppercase", display: "block", marginBottom: 10 }}>Bid Currency (pick one)</label>
                <div style={{ display: "flex", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" checked={form.bid_currency === "auec"} onChange={() => setForm(f => ({ ...f, bid_currency: "auec" }))} />
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#4fc3f7" }}>aUEC</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" checked={form.bid_currency === "merits"} onChange={() => setForm(f => ({ ...f, bid_currency: "merits" }))} />
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#fcd657" }}>Merits</span>
                  </label>
                </div>
              </div>
              <div>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#a78bfa88", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Starting Bid</label>
                <input type="number" value={form.starting_bid} onChange={e => setForm(f => ({ ...f, starting_bid: e.target.value }))} placeholder="Minimum bid amount" style={inputStyle} min="0" />
              </div>
              <div>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#a78bfa88", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Auction Duration (days)</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {["1", "3", "5", "7", "14"].map(d => (
                    <button key={d} type="button" onClick={() => setForm(f => ({ ...f, auction_days: d }))} style={{
                      padding: "8px 16px", fontFamily: "'Orbitron', sans-serif", fontSize: 10,
                      border: `1px solid ${form.auction_days === d ? "#a78bfa" : "rgba(255,255,255,0.1)"}`,
                      color: form.auction_days === d ? "#a78bfa" : "rgba(232,234,246,0.4)",
                      background: form.auction_days === d ? "rgba(167,139,250,0.1)" : "transparent", cursor: "pointer",
                    }}>{d}d</button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Pricing model toggle — Manual aUEC vs Merit-linked */}
              <div>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 10 }}>Pricing Model</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={() => setForm(f => ({ ...f, pricing_mode: "manual" }))}
                    style={{ flex: "1 1 200px", padding: "10px 14px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 1.5, textAlign: "left",
                      background: form.pricing_mode === "manual" ? `${ACCENT}18` : "transparent",
                      border: `1px solid ${form.pricing_mode === "manual" ? ACCENT : "rgba(255,255,255,0.12)"}`,
                      color: form.pricing_mode === "manual" ? ACCENT : "rgba(232,234,246,0.55)",
                      cursor: "pointer" }}>
                    Manual aUEC
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, fontWeight: 400, letterSpacing: 0, marginTop: 2, color: "rgba(232,234,246,0.45)", textTransform: "none" }}>You set the aUEC price directly.</div>
                  </button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, pricing_mode: "merit_linked" }))}
                    disabled={!meritPegAuec}
                    title={!meritPegAuec ? "A super-admin must set the Merit peg in the admin UEX widget first." : ""}
                    style={{ flex: "1 1 200px", padding: "10px 14px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 1.5, textAlign: "left",
                      background: form.pricing_mode === "merit_linked" ? `${ACCENT}18` : "transparent",
                      border: `1px solid ${form.pricing_mode === "merit_linked" ? ACCENT : "rgba(255,255,255,0.12)"}`,
                      color: form.pricing_mode === "merit_linked" ? ACCENT : "rgba(232,234,246,0.55)",
                      cursor: meritPegAuec ? "pointer" : "not-allowed", opacity: meritPegAuec ? 1 : 0.5 }}>
                    Merit-linked
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, fontWeight: 400, letterSpacing: 0, marginTop: 2, color: "rgba(232,234,246,0.45)", textTransform: "none" }}>
                      {meritPegAuec ? `aUEC = merit_price × ${Number(meritPegAuec).toLocaleString()}` : "Peg not set"}
                    </div>
                  </button>
                </div>
                {form.pricing_mode === "merit_linked" && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: `${ACCENT}08`, border: `1px solid ${ACCENT}33`, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.6)" }}>
                    Merit price is the source of truth. The aUEC equivalent updates automatically whenever super-admins re-peg the Merit value, so this listing tracks the inflationary index.
                  </div>
                )}
              </div>

              {/* Currency toggles (fixed price) — merit-linked auto-enables both currencies */}
              <div style={{ display: form.pricing_mode === "merit_linked" ? "none" : "block" }}>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 10 }}>Accepted Payment</label>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.accepts_auec} onChange={e => setForm(f => ({ ...f, accepts_auec: e.target.checked }))} />
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#4fc3f7" }}>aUEC</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.accepts_merits} onChange={e => setForm(f => ({ ...f, accepts_merits: e.target.checked }))} />
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#fcd657" }}>Merits</span>
                  </label>
                </div>
              </div>

              {form.accepts_auec && form.pricing_mode !== "merit_linked" && (
                <div>
                  <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#4fc3f788", textTransform: "uppercase", display: "block", marginBottom: 6 }}>aUEC Price</label>
                  <input type="number" value={form.auec_price} onChange={e => setForm(f => ({ ...f, auec_price: e.target.value }))} placeholder="e.g. 50000" style={inputStyle} min="0" />
                </div>
              )}
              {(form.accepts_merits || form.pricing_mode === "merit_linked") && (
                <div>
                  <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#fcd65788", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Merit Price</label>
                  <input type="number" value={form.merit_price} onChange={e => setForm(f => ({ ...f, merit_price: e.target.value }))} placeholder="e.g. 100" style={inputStyle} min="0" />
                  {form.pricing_mode === "merit_linked" && meritPegAuec && parseInt(form.merit_price) > 0 && (
                    <div style={{ marginTop: 6, fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#4fc3f7", letterSpacing: 1 }}>
                      = {Math.round(parseInt(form.merit_price) * meritPegAuec).toLocaleString()} aUEC at current peg
                    </div>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", display: "block", marginBottom: 10 }}>Listing Type</label>
                <div style={{ display: "flex", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" checked={!form.is_permanent} onChange={() => setForm(f => ({ ...f, is_permanent: false }))} />
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6" }}>Limited Quantity</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" checked={form.is_permanent} onChange={() => setForm(f => ({ ...f, is_permanent: true }))} />
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6" }}>Permanent</span>
                  </label>
                </div>
                {!form.is_permanent && (
                  <input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Quantity" style={{ ...inputStyle, marginTop: 8, maxWidth: 120 }} min="1" />
                )}
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button onClick={handleSubmit} disabled={saving || !form.title.trim()} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "12px 24px",
              background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: saving ? "wait" : "pointer", opacity: !form.title.trim() ? 0.4 : 1,
            }}>{saving ? "Creating..." : "Create Listing"}</button>
            <button onClick={onClose} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, padding: "12px 16px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bounty Modal (create + edit) ────────────────────────────────────────────
// `existing` (optional) — pass a bounty listing to switch into edit mode.
function CreateBountyModal({ onClose, onCreated, profile, sections, existing = null }) {
  const BOUNTY = "#ce93d8";
  const isEdit = !!existing;
  const [form, setForm] = useState(() => existing ? {
    title: existing.title || "",
    description: existing.description || "",
    image_url: existing.image_url || "",
    section_id: existing.section_id || "",
    accepts_merits: !!existing.accepts_merits,
    accepts_auec:   !!existing.accepts_auec,
    accepts_cash:   !!existing.accepts_cash,
    merit_price: existing.merit_price ?? "",
    auec_price:  existing.auec_price ?? "",
    cash_price:  existing.cash_price ?? "",
    is_permanent: !!existing.is_permanent,
    quantity: existing.quantity != null ? String(existing.quantity) : "1",
    sc_item_id: existing.sc_item_id || null,
    sc_item_source_url: "",
  } : {
    title: "", description: "", image_url: "", section_id: "",
    accepts_merits: false, accepts_auec: true, accepts_cash: false,
    merit_price: "", auec_price: "", cash_price: "",
    is_permanent: false, quantity: "1",
    sc_item_id: null, sc_item_source_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInputRef = React.useRef(null);

  // If we're editing a bounty that's already linked to an SC Item, fetch the
  // source_url so we can show the wiki link in the picker chip.
  useEffect(() => {
    if (!existing?.sc_item_id) return;
    let cancelled = false;
    supabase.from("sc_items").select("source_url").eq("id", existing.sc_item_id).maybeSingle()
      .then(({ data }) => { if (!cancelled && data?.source_url) setForm(f => ({ ...f, sc_item_source_url: data.source_url })); });
    return () => { cancelled = true; };
  }, [existing?.sc_item_id]);

  function applyCatalogPick(item) {
    if (!item) return;
    setForm(f => ({
      ...f,
      title: item.name || f.title,
      description: item.description || f.description,
      image_url: item.image_url || f.image_url,
      sc_item_id: item.id,
      sc_item_source_url: item.source_url || "",
    }));
    setPickerOpen(false);
  }

  const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/avif"];

  async function uploadFile(file) {
    if (!file || !ACCEPTED_TYPES.includes(file.type)) return;
    setUploading(true);
    const ext = file.name.split(".").pop().toLowerCase();
    const path = `bounty/${profile.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("store-media").upload(path, file, { contentType: file.type });
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from("store-media").getPublicUrl(path);
      setForm(f => ({ ...f, image_url: publicUrl }));
    }
    setUploading(false);
  }
  function handleImageUpload(e) { uploadFile(e.target.files?.[0]); }
  function handleDrop(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); const file = e.dataTransfer.files?.[0]; if (file && ACCEPTED_TYPES.includes(file.type)) uploadFile(file); }
  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragOver(true); }
  function handleDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); }

  async function handleSubmit() {
    if (!form.title.trim()) return;
    setSaving(true);
    const newQty = form.is_permanent ? null : parseInt(form.quantity) || 1;
    const payload = {
      store_type: "bounty",
      section_id: form.section_id || null,
      title: form.title.trim(),
      description: form.description.trim(),
      sc_item_id: form.sc_item_id || null,
      image_url: form.image_url,
      accepts_merits: form.accepts_merits,
      accepts_auec: form.accepts_auec,
      accepts_cash: form.accepts_cash,
      merit_price: form.accepts_merits ? parseInt(form.merit_price) || null : null,
      auec_price: form.accepts_auec ? parseInt(form.auec_price) || null : null,
      cash_price: form.accepts_cash ? parseFloat(form.cash_price) || null : null,
      is_permanent: form.is_permanent,
      quantity: newQty,
    };

    let error;
    if (isEdit) {
      // Preserve the original creator and contract counts; just patch editable fields.
      // If the new quantity drops at or below quantity_sold, mark the bounty fulfilled.
      const updates = { ...payload, updated_at: new Date().toISOString() };
      const sold = existing.quantity_sold || 0;
      if (!form.is_permanent && newQty != null && newQty <= sold) {
        updates.status = "sold_out";
      } else if (existing.status === "sold_out" && (form.is_permanent || (newQty != null && newQty > sold))) {
        // Re-opened by raising the quantity — flip back to active.
        updates.status = "active";
      }
      ({ error } = await supabase.from("store_listings").update(updates).eq("id", existing.id));
    } else {
      const insertPayload = {
        ...payload,
        listed_by: profile.id,
        is_auction: false,
        auction_end: null,
        bid_currency: null,
        starting_bid: null,
        current_bid: null,
        current_bidder_id: null,
      };
      ({ error } = await supabase.from("store_listings").insert([insertPayload]));
    }
    setSaving(false);
    if (!error) { onCreated(); onClose(); }
  }

  const inputStyle = { width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${BOUNTY}44`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: "#0a0f1c", border: `1px solid ${BOUNTY}66`, padding: 32, maxWidth: 520, width: "100%", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <SectionLabel color={BOUNTY}>{isEdit ? "Edit Bounty" : "New Bounty"}</SectionLabel>
        <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: "#e8eaf6", marginBottom: 8 }}>{isEdit ? "Edit Your Bounty" : "Post a Buy Request"}</h3>
        <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.45)", marginBottom: 20 }}>
          {isEdit
            ? "Update the details below. Existing contracts that have been taken on this bounty are unaffected."
            : "Tell the org what you're looking to buy and what you'll pay. A seller can claim the contract to start a private negotiation thread."}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <CatalogPickerRow form={form} setForm={setForm} pickerOpen={pickerOpen} setPickerOpen={setPickerOpen} applyCatalogPick={applyCatalogPick} accent={BOUNTY} />

          {form.sc_item_id && form.title && (
            <SuggestedPricing
              itemName={form.title}
              accent={BOUNTY}
              onApply={(auec) => {
                // Bounties accept whatever currencies the user toggled. Fill the
                // aUEC offer field by default; user can still type over.
                setForm(f => ({ ...f, accepts_auec: true, auec_price: String(auec) }));
              }}
            />
          )}

          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY}cc`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Title</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="What are you looking to buy?" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY}cc`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Quality, condition, delivery preferences, deadlines..." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          {sections.length > 0 && (
            <div>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY}cc`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Category</label>
              <select value={form.section_id} onChange={e => setForm(f => ({ ...f, section_id: e.target.value }))} style={{ ...inputStyle, background: "#0a0610" }}>
                <option value="">None</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY}cc`, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Image (optional)</label>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/avif" onChange={handleImageUpload} style={{ display: "none" }} />
            {form.image_url ? (
              <div style={{ position: "relative", width: 120, height: 120, border: `1px solid ${BOUNTY}44`, background: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={() => fileInputRef.current?.click()}>
                <img src={form.image_url} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button onClick={e => { e.stopPropagation(); setForm(f => ({ ...f, image_url: "" })); }} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#ef5350", border: "none", color: "#fff", fontSize: 10, cursor: "pointer" }}>×</button>
              </div>
            ) : (
              <div
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? BOUNTY : BOUNTY + "55"}`, background: dragOver ? `${BOUNTY}10` : "rgba(255,255,255,0.02)", padding: "22px 16px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
              >
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 20, color: `${BOUNTY}${dragOver ? "" : "55"}`, marginBottom: 6 }}>⬡</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: dragOver ? BOUNTY : "rgba(232,234,246,0.4)" }}>
                  {uploading ? "Uploading..." : dragOver ? "Drop image here" : "Drag & drop or click to upload"}
                </div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", letterSpacing: 1, marginTop: 4 }}>PNG, JPG, GIF, WebP</div>
              </div>
            )}
          </div>

          {/* Payment options */}
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY}cc`, textTransform: "uppercase", display: "block", marginBottom: 10 }}>What You'll Pay</label>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={form.accepts_auec} onChange={e => setForm(f => ({ ...f, accepts_auec: e.target.checked }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#4fc3f7" }}>aUEC</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={form.accepts_merits} onChange={e => setForm(f => ({ ...f, accepts_merits: e.target.checked }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#fcd657" }}>Merits</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={form.accepts_cash} onChange={e => setForm(f => ({ ...f, accepts_cash: e.target.checked }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#34d399" }}>Cash</span>
              </label>
            </div>
          </div>

          {form.accepts_auec && (
            <div>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#4fc3f7cc", textTransform: "uppercase", display: "block", marginBottom: 6 }}>aUEC Offered (per unit)</label>
              <input type="number" value={form.auec_price} onChange={e => setForm(f => ({ ...f, auec_price: e.target.value }))} placeholder="e.g. 50000" style={inputStyle} min="0" />
            </div>
          )}
          {form.accepts_merits && (
            <div>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#fcd657cc", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Merits Offered (per unit)</label>
              <input type="number" value={form.merit_price} onChange={e => setForm(f => ({ ...f, merit_price: e.target.value }))} placeholder="e.g. 100" style={inputStyle} min="0" />
            </div>
          )}
          {form.accepts_cash && (
            <div>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#34d399cc", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Cash Offered (per unit, USD)</label>
              <input type="number" step="0.01" value={form.cash_price} onChange={e => setForm(f => ({ ...f, cash_price: e.target.value }))} placeholder="e.g. 10.00" style={inputStyle} min="0" />
            </div>
          )}

          {/* Quantity */}
          <div>
            <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY}cc`, textTransform: "uppercase", display: "block", marginBottom: 10 }}>Bounty Type</label>
            <div style={{ display: "flex", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" checked={!form.is_permanent} onChange={() => setForm(f => ({ ...f, is_permanent: false }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6" }}>Limited Quantity</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" checked={form.is_permanent} onChange={() => setForm(f => ({ ...f, is_permanent: true }))} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6" }}>Open-ended (standing bounty)</span>
              </label>
            </div>
            {!form.is_permanent && (
              <input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Quantity" style={{ ...inputStyle, marginTop: 8, maxWidth: 120 }} min="1" />
            )}
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button onClick={handleSubmit} disabled={saving || !form.title.trim()} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "12px 24px",
              background: `${BOUNTY}22`, border: `1px solid ${BOUNTY}`, color: BOUNTY, cursor: saving ? "wait" : "pointer", opacity: !form.title.trim() ? 0.4 : 1,
            }}>{saving ? (isEdit ? "Saving..." : "Posting...") : (isEdit ? "Save Changes" : "Post Bounty")}</button>
            <button onClick={onClose} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, padding: "12px 16px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Countdown Timer ─────────────────────────────────────────────────────────
function AuctionCountdown({ endTime }) {
  const [remaining, setRemaining] = useState("");
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    function tick() {
      const diff = new Date(endTime) - Date.now();
      if (diff <= 0) { setRemaining("ENDED"); setExpired(true); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    }
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  return (
    <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, color: expired ? "#ef5350" : "#a78bfa", letterSpacing: 1 }}>{remaining}</span>
  );
}

// ─── Listing Detail View ─────────────────────────────────────────────────────
function ListingDetail({ listing, onBack, profiles, onAddToCart, meritBalance, profile, onPlaceBid, bids, onTakeContract, onShareListing, onEditListing }) {
  const [selectedPayment, setSelectedPayment] = useState(
    listing.accepts_merits ? "merits" : listing.accepts_auec ? "auec" : "cash"
  );
  const [qty, setQty] = useState(1);
  const [bidAmount, setBidAmount] = useState("");
  const [bidding, setBidding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [takingContract, setTakingContract] = useState(false);
  const [takeResult, setTakeResult] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [discordThreads, setDiscordThreads] = useState([]); // [{ thread_url, channel_id, created_at }]
  const seller = profiles.find(p => p.id === listing.listed_by);
  const maxQty = listing.is_permanent ? 99 : Math.max(0, (listing.quantity || 0) - listing.quantity_sold);
  const isOwnListing = profile?.id === listing.listed_by;
  const isAuction = listing.is_auction;
  const isBounty = listing.store_type === "bounty";
  const BOUNTY_COLOR = "#ce93d8";
  const canShare = ["marketplace", "bounty", "merit", "swag"].includes(listing.store_type);
  const auctionEnded = isAuction && listing.auction_end && new Date(listing.auction_end) <= new Date();
  const minBid = (listing.current_bid || listing.starting_bid || 0) + 1;
  const listingBids = (bids || []).filter(b => b.listing_id === listing.id);
  const currentBidder = listing.current_bidder_id ? profiles.find(p => p.id === listing.current_bidder_id) : null;

  async function handleBid() {
    const amount = parseInt(bidAmount);
    if (!amount || amount < minBid) return;
    setBidding(true);
    await onPlaceBid(listing.id, amount, listing.bid_currency);
    setBidding(false);
    setBidAmount("");
  }

  async function handleTakeContract() {
    if (!onTakeContract) return;
    setTakingContract(true);
    setTakeResult(null);
    const result = await onTakeContract(listing.id, qty);
    setTakingContract(false);
    setTakeResult(result);
    // Drop the new contract thread into the link box immediately
    if (result?.ok && result.thread_url) {
      setDiscordThreads(prev => [
        { thread_url: result.thread_url, channel_id: null, created_at: new Date().toISOString(), kind: "contract" },
        ...prev,
      ]);
    }
  }

  async function handleShareListing() {
    if (!onShareListing) return;
    setSharing(true);
    setShareResult(null);
    const result = await onShareListing(listing.id);
    setSharing(false);
    setShareResult(result);
    // Pull the freshly-created thread URLs into the list of links shown below
    if (result?.ok && Array.isArray(result.results)) {
      const fresh = result.results
        .filter(r => r.ok && r.thread_url)
        .map(r => ({ thread_url: r.thread_url, channel_id: r.channel_id, created_at: new Date().toISOString() }));
      if (fresh.length) setDiscordThreads(prev => [...fresh, ...prev]);
    }
  }

  // Load any existing Discord threads this listing has — share threads from
  // marketplace_shares plus, for bounties, contract threads from bounty_contracts.
  // Survives a page refresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sharesPromise = supabase
        .from("marketplace_shares")
        .select("discord_thread_url, discord_channel_id, created_at")
        .eq("listing_id", listing.id)
        .order("created_at", { ascending: false });
      const contractsPromise = listing.store_type === "bounty"
        ? supabase
            .from("bounty_contracts")
            .select("discord_thread_url, discord_channel_id, created_at")
            .eq("listing_id", listing.id)
            .order("created_at", { ascending: false })
        : Promise.resolve({ data: [] });
      const [{ data: shares }, { data: contracts }] = await Promise.all([sharesPromise, contractsPromise]);
      if (cancelled) return;
      const merged = [
        ...(shares || []).filter(r => !!r.discord_thread_url).map(r => ({ thread_url: r.discord_thread_url, channel_id: r.discord_channel_id, created_at: r.created_at, kind: "share" })),
        ...(contracts || []).filter(r => !!r.discord_thread_url).map(r => ({ thread_url: r.discord_thread_url, channel_id: r.discord_channel_id, created_at: r.created_at, kind: "contract" })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setDiscordThreads(merged);
    })();
    return () => { cancelled = true; };
  }, [listing.id, listing.store_type]);

  function copyShareLink() {
    const url = `${window.location.origin}/share/listing/${listing.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  return (
    <div>
      <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, color: `${ACCENT}88`, background: "none", border: "none", cursor: "pointer", marginBottom: 24, textTransform: "uppercase" }}>← Back to listings</button>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(400px, 100%), 1fr))", gap: 32 }}>
        {/* Image */}
        <div style={{ aspectRatio: "16/9", background: "rgba(0,0,0,0.3)", overflow: "hidden", clipPath: "polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 0 100%)" }}>
          {listing.image_url ? (
            <img src={listing.image_url} alt={listing.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 48, color: `${ACCENT}12` }}>◇</div>
          )}
        </div>

        {/* Details */}
        <div>
          <SectionLabel color={isBounty ? BOUNTY_COLOR : ACCENT}>{listing.store_type === "merit" ? "Merit Store" : listing.store_type === "marketplace" ? "Player Market" : listing.store_type === "bounty" ? "Bounty / Buy Request" : "Swag Store"}</SectionLabel>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 3vw, 32px)", color: "#e8eaf6", marginBottom: 12 }}>{listing.title}</h2>
          {listing.sc_item_id && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, padding: "10px 14px", border: `1px solid ${ACCENT}33`, background: `${ACCENT}08` }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, color: ACCENT, textTransform: "uppercase" }}>SC Items Catalog</span>
              {(listing._sc_group || listing._sc_category) && (
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.5)" }}>
                  {[listing._sc_group, listing._sc_category].filter(Boolean).join(" · ")}
                </span>
              )}
              {listing._sc_source_url && (
                <a href={listing._sc_source_url} target="_blank" rel="noreferrer"
                  style={{ marginLeft: "auto", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: ACCENT, textTransform: "uppercase", textDecoration: "none", padding: "4px 10px", border: `1px solid ${ACCENT}55` }}>
                  View on Wiki ↗
                </a>
              )}
            </div>
          )}
          {listing.description && <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.55)", lineHeight: 1.7, marginBottom: 20 }}>{listing.description}</p>}

          {seller && (
            <Link to={`/profile/${seller.id}`} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, textDecoration: "none" }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", border: `2px solid ${isBounty ? BOUNTY_COLOR : ACCENT}44` }}>
                {seller.discord_avatar ? <img src={seller.discord_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 12, color: isBounty ? BOUNTY_COLOR : ACCENT }}>{seller.discord_username?.charAt(0)}</div>}
              </div>
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "rgba(232,234,246,0.7)" }}>{isBounty ? "Posted by" : "Listed by"} {seller.discord_username}</span>
            </Link>
          )}

          {!isAuction && <PriceBadge listing={listing} />}
          <div style={{ marginTop: 12 }}><StatusBadge listing={listing} /></div>

          {/* Existing Discord threads — only renders if this listing has been shared at least once */}
          {discordThreads.length > 0 && (
            <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(88,101,242,0.08)", border: "1px solid rgba(88,101,242,0.35)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "#5865f2", textTransform: "uppercase", marginBottom: 8 }}>
                On Discord
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {discordThreads.map((t, i) => (
                  <a key={`${t.thread_url}-${i}`} href={t.thread_url} target="_blank" rel="noopener noreferrer" style={{
                    fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
                    color: "#5865f2", background: "rgba(88,101,242,0.10)", border: "1px solid rgba(88,101,242,0.45)",
                    padding: "5px 12px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(88,101,242,0.18)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(88,101,242,0.10)"; }}
                  >
                    <span style={{ fontSize: 11 }}>↗</span>
                    View thread on Discord
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Share with Org */}
          {canShare && profile && (
            <div style={{ marginTop: 14 }}>
              <button onClick={handleShareListing} disabled={sharing} style={{
                fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
                color: "#4fc3f7", background: "rgba(79,195,247,0.08)", border: "1px solid rgba(79,195,247,0.4)",
                padding: "6px 14px", cursor: sharing ? "wait" : "pointer",
              }}>{sharing ? "Sharing..." : "📣 Share with Org"}</button>
              {shareResult && shareResult.ok && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.35)" }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "#34d399", textTransform: "uppercase", marginBottom: 4 }}>
                    ✓ Shared to {(shareResult.results || []).filter(r => r.ok).length} channel{(shareResult.results || []).filter(r => r.ok).length !== 1 ? "s" : ""}
                    {(shareResult.skipped || []).length > 0 && ` · ${shareResult.skipped.length} already shared`}
                  </div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.7)" }}>
                    Check the org share channels on Discord.
                  </div>
                </div>
              )}
              {shareResult && !shareResult.ok && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(239,83,80,0.08)", border: "1px solid rgba(239,83,80,0.35)", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#ef5350" }}>
                  {shareResult.error || "Could not share listing."}
                </div>
              )}
            </div>
          )}

          {/* Share link for auctions */}
          {isAuction && (
            <div style={{ marginTop: 12 }}>
              <button onClick={copyShareLink} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "#a78bfa", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.3)", padding: "6px 14px", cursor: "pointer" }}>
                {copied ? "✓ LINK COPIED" : "COPY SHARE LINK"}
              </button>
            </div>
          )}

          {/* Auction bidding section */}
          {isAuction && (
            <div style={{ marginTop: 24, padding: 20, border: "1px solid rgba(167,139,250,0.25)", background: "rgba(167,139,250,0.04)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#a78bfa88", textTransform: "uppercase", marginBottom: 12 }}>Auction</div>

              {/* Timer */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, padding: "10px 14px", background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.15)" }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.5)" }}>Time Remaining</span>
                <AuctionCountdown endTime={listing.auction_end} />
              </div>

              {/* Current bid */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "10px 14px", background: "rgba(167,139,250,0.03)" }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.5)" }}>Current Bid</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 16, fontWeight: 900, color: listing.bid_currency === "merits" ? "#fcd657" : "#4fc3f7" }}>
                  {listing.current_bid != null ? (listing.bid_currency === "merits" ? `${listing.current_bid} Merits` : `${Number(listing.current_bid).toLocaleString()} aUEC`) : "No bids yet"}
                </span>
              </div>
              {currentBidder && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, padding: "6px 14px" }}>
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.35)" }}>Leading Bidder</span>
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, fontWeight: 600, color: "#e8eaf6" }}>{currentBidder.discord_username}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, padding: "6px 14px" }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.35)" }}>Starting Bid</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.4)" }}>
                  {listing.bid_currency === "merits" ? `${listing.starting_bid || 0} Merits` : `${Number(listing.starting_bid || 0).toLocaleString()} aUEC`}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, padding: "6px 14px" }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.35)" }}>Total Bids</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.4)" }}>{listingBids.length}</span>
              </div>

              {/* Place bid */}
              {!auctionEnded && !isOwnListing && profile && (
                <div>
                  {listing.bid_currency === "merits" && meritBalance && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, padding: "8px 12px", background: "rgba(252,214,87,0.06)", border: "1px solid rgba(252,214,87,0.15)" }}>
                      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.5)" }}>Your Available Merits</span>
                      <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, color: "#fcd657" }}>{meritBalance.available}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)} placeholder={`Min bid: ${minBid}`} min={minBid} style={{ flex: 1, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.3)", color: "#e8eaf6", fontFamily: "'Orbitron', sans-serif", fontSize: 14, borderRadius: 2 }} />
                    <button onClick={handleBid} disabled={bidding || !bidAmount || parseInt(bidAmount) < minBid} style={{
                      padding: "10px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
                      background: "rgba(167,139,250,0.15)", border: "1px solid #a78bfa", color: "#a78bfa", cursor: bidding ? "wait" : "pointer",
                      opacity: (!bidAmount || parseInt(bidAmount) < minBid) ? 0.4 : 1,
                    }}>{bidding ? "..." : "BID"}</button>
                  </div>
                </div>
              )}
              {auctionEnded && (
                <div style={{ textAlign: "center", padding: 12, fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, color: "#ef5350" }}>AUCTION ENDED</div>
              )}

              {/* Bid history */}
              {listingBids.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(167,139,250,0.5)", textTransform: "uppercase", marginBottom: 8 }}>Bid History</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {listingBids.slice(0, 10).map((b, i) => {
                      const bidder = profiles.find(p => p.id === b.bidder_id);
                      return (
                        <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: i === 0 ? "rgba(167,139,250,0.06)" : "transparent", border: i === 0 ? "1px solid rgba(167,139,250,0.15)" : "none" }}>
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: i === 0 ? "#e8eaf6" : "rgba(232,234,246,0.4)" }}>{bidder?.discord_username || "Unknown"}</span>
                          <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: b.currency === "merits" ? "#fcd657" : "#4fc3f7" }}>
                            {b.currency === "merits" ? `${b.amount} M` : `${Number(b.amount).toLocaleString()} aUEC`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bounty — Take Contract section */}
          {isBounty && listing.status !== "sold_out" && !isOwnListing && (
            <div style={{ marginTop: 24, padding: 20, border: `1px solid ${BOUNTY_COLOR}44`, background: `${BOUNTY_COLOR}0a` }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${BOUNTY_COLOR}cc`, textTransform: "uppercase", marginBottom: 12 }}>Accept this bounty</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", marginBottom: 16, lineHeight: 1.5 }}>
                Taking this contract pings you and the buyer in a new thread in the org sales channel. Coordinate delivery and payment there.
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
                {listing.accepts_merits && listing.merit_price != null && (
                  <div style={{ padding: "8px 14px", border: "1px solid rgba(252,214,87,0.4)", background: "rgba(252,214,87,0.08)" }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "#fcd657aa" }}>MERITS / UNIT</div>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: "#fcd657" }}>{listing.merit_price}</div>
                  </div>
                )}
                {listing.accepts_auec && listing.auec_price != null && (
                  <div style={{ padding: "8px 14px", border: "1px solid rgba(79,195,247,0.4)", background: "rgba(79,195,247,0.08)" }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "#4fc3f7aa" }}>aUEC / UNIT</div>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: "#4fc3f7" }}>{Number(listing.auec_price).toLocaleString()}</div>
                  </div>
                )}
                {listing.accepts_cash && listing.cash_price != null && (
                  <div style={{ padding: "8px 14px", border: "1px solid rgba(52,211,153,0.4)", background: "rgba(52,211,153,0.08)" }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "#34d399aa" }}>CASH / UNIT</div>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: "#34d399" }}>${Number(listing.cash_price).toFixed(2)}</div>
                  </div>
                )}
              </div>

              {!listing.is_permanent && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>COMMIT QTY</span>
                  <input type="number" value={qty} onChange={e => setQty(Math.max(1, Math.min(maxQty, parseInt(e.target.value) || 1)))} min="1" max={maxQty} style={{ width: 80, padding: "6px 10px", background: "rgba(255,255,255,0.04)", border: `1px solid ${BOUNTY_COLOR}44`, color: "#e8eaf6", fontFamily: "'Orbitron'", fontSize: 14, textAlign: "center", borderRadius: 2 }} />
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1 }}>of {maxQty} remaining</span>
                </div>
              )}

              <button onClick={handleTakeContract} disabled={takingContract || !profile || maxQty < 1} style={{
                width: "100%", padding: "14px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase",
                background: `${BOUNTY_COLOR}22`, border: `1px solid ${BOUNTY_COLOR}`, color: BOUNTY_COLOR, cursor: takingContract ? "wait" : "pointer", transition: "all 0.2s",
                opacity: (!profile || maxQty < 1) ? 0.4 : 1,
              }}>{takingContract ? "STARTING THREAD..." : "TAKE CONTRACT"}</button>

              {takeResult && takeResult.ok && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.35)" }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "#34d399", textTransform: "uppercase", marginBottom: 4 }}>✓ Contract Started</div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.7)" }}>
                    Check the sales channel on Discord — a new thread has been opened with both of you.
                  </div>
                </div>
              )}
              {takeResult && !takeResult.ok && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(239,83,80,0.08)", border: "1px solid rgba(239,83,80,0.35)", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#ef5350" }}>
                  {takeResult.error || "Could not start contract. Please try again."}
                </div>
              )}
            </div>
          )}

          {/* Fixed price purchase options (non-auction, non-bounty) */}
          {!isAuction && !isBounty && listing.status !== "sold_out" && !isOwnListing && (
            <div style={{ marginTop: 24, padding: 20, border: `1px solid ${ACCENT}22`, background: `${ACCENT}04` }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", marginBottom: 12 }}>Purchase Options</div>

              {/* Payment type */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {listing.accepts_merits && (
                  <button onClick={() => setSelectedPayment("merits")} style={{ padding: "8px 16px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1, border: `1px solid ${selectedPayment === "merits" ? "#fcd657" : "rgba(255,255,255,0.1)"}`, color: selectedPayment === "merits" ? "#fcd657" : "rgba(232,234,246,0.4)", background: selectedPayment === "merits" ? "rgba(252,214,87,0.1)" : "transparent", cursor: "pointer" }}>MERITS</button>
                )}
                {listing.accepts_auec && (
                  <button onClick={() => setSelectedPayment("auec")} style={{ padding: "8px 16px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1, border: `1px solid ${selectedPayment === "auec" ? "#4fc3f7" : "rgba(255,255,255,0.1)"}`, color: selectedPayment === "auec" ? "#4fc3f7" : "rgba(232,234,246,0.4)", background: selectedPayment === "auec" ? "rgba(79,195,247,0.1)" : "transparent", cursor: "pointer" }}>aUEC</button>
                )}
                {listing.accepts_cash && (
                  <button onClick={() => setSelectedPayment("cash")} style={{ padding: "8px 16px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1, border: `1px solid ${selectedPayment === "cash" ? "#34d399" : "rgba(255,255,255,0.1)"}`, color: selectedPayment === "cash" ? "#34d399" : "rgba(232,234,246,0.4)", background: selectedPayment === "cash" ? "rgba(52,211,153,0.1)" : "transparent", cursor: "pointer" }}>CASH</button>
                )}
              </div>

              {/* Quantity */}
              {!listing.is_permanent && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>QTY</span>
                  <input type="number" value={qty} onChange={e => setQty(Math.max(1, Math.min(maxQty, parseInt(e.target.value) || 1)))} min="1" max={maxQty} style={{ width: 60, padding: "6px 10px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Orbitron'", fontSize: 14, textAlign: "center", borderRadius: 2 }} />
                </div>
              )}

              {/* Merit balance check */}
              {selectedPayment === "merits" && meritBalance && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, padding: "8px 12px", background: "rgba(252,214,87,0.06)", border: "1px solid rgba(252,214,87,0.15)" }}>
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.5)" }}>Available Merits</span>
                  <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, color: "#fcd657" }}>{meritBalance.available}</span>
                </div>
              )}
              {selectedPayment === "merits" && meritBalance && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, padding: "8px 12px", background: "rgba(252,214,87,0.03)" }}>
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.5)" }}>Pending</span>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "rgba(252,214,87,0.5)" }}>{meritBalance.pending}</span>
                </div>
              )}

              <button onClick={() => onAddToCart(listing.id, selectedPayment, qty)} style={{
                width: "100%", padding: "14px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase",
                background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", transition: "all 0.2s",
              }}>ADD TO CART</button>
            </div>
          )}

          {isOwnListing && isBounty && onEditListing && (
            <div style={{ marginTop: 24, padding: 16, border: `1px solid ${BOUNTY_COLOR}33`, background: `${BOUNTY_COLOR}08`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.65)" }}>
                This is your bounty. You can edit the title, description, rate, and quantity.
              </div>
              <button onClick={() => onEditListing(listing)} style={{
                fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
                padding: "8px 18px", background: `${BOUNTY_COLOR}22`, border: `1px solid ${BOUNTY_COLOR}`,
                color: BOUNTY_COLOR, cursor: "pointer",
              }}>Edit Bounty</button>
            </div>
          )}
          {isOwnListing && !isBounty && (
            <div style={{ marginTop: 24, padding: 16, border: "1px solid rgba(79,195,247,0.2)", background: "rgba(79,195,247,0.04)", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(79,195,247,0.7)" }}>
              This is your listing. Manage it from your profile.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN MARKET PAGE ────────────────────────────────────────────────────────
export default function MarketPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [activeTab, setActiveTab] = useState("merit");
  const [listings, setListings] = useState([]);
  const [sections, setSections] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSection, setFilterSection] = useState("");

  // Cart state
  const [cartItems, setCartItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  // Merit balance
  const [meritBalance, setMeritBalance] = useState(null);

  // Merit-to-aUEC peg (from app_settings) — listings with pricing_mode='merit_linked'
  // have their displayed aUEC computed live as merit_price × meritPegAuec. When a
  // super-admin re-pegs in the admin UEX widget, those listings instantly reflect
  // the new value without sellers having to edit each one.
  const [meritPegAuec, setMeritPegAuec] = useState(null);

  // Create listing modal
  const [showCreateListing, setShowCreateListing] = useState(false);
  const [showCreateBounty, setShowCreateBounty] = useState(false);
  const [editingBounty, setEditingBounty] = useState(null); // pass a bounty listing to open the form in edit mode

  // Bids data
  const [bids, setBids] = useState([]);

  // Browse All filters
  const [browseSort, setBrowseSort] = useState("newest");
  const [showCashItems, setShowCashItems] = useState(false);

  // Selected listing for detail view
  const [selectedListing, setSelectedListing] = useState(listingId || null);

  useEffect(() => { setSelectedListing(listingId || null); }, [listingId]);

  // Load all data
  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: l }, { data: s }, { data: p }, { data: c }, { data: b }, { data: peg }] = await Promise.all([
      supabase.from("store_listings").select("*").neq("status", "removed").order("created_at", { ascending: false }),
      supabase.from("store_sections").select("*").order("sort_order"),
      supabase.from("profiles").select("id, discord_username, discord_avatar, org_role_sort").order("discord_username"),
      user ? supabase.from("store_cart_items").select("*").eq("user_id", user.id) : { data: [] },
      supabase.from("store_bids").select("*").order("created_at", { ascending: false }).limit(500),
      supabase.from("app_settings").select("key, value").eq("key", "merit_value_auec").maybeSingle(),
    ]);
    const pegVal = peg ? parseFloat(peg.value) : NaN;
    setMeritPegAuec(Number.isFinite(pegVal) && pegVal > 0 ? pegVal : null);
    // Compute the live aUEC value for merit-linked listings so the marketplace
    // always reflects the current peg without sellers having to re-edit each row.
    const pegForCompute = Number.isFinite(pegVal) && pegVal > 0 ? pegVal : null;
    // Enrich listings linked to the SC Items catalog with their wiki source_url
    // so ListingCard / ListingDetail can render a "Wiki ↗" link without an
    // extra per-row roundtrip. One batched query for all linked items.
    const scItemIds = Array.from(new Set((l || []).map(r => r.sc_item_id).filter(Boolean)));
    let scItemMap = {};
    if (scItemIds.length > 0) {
      const { data: scRows } = await supabase.from("sc_items")
        .select("id, source_url, item_group, category")
        .in("id", scItemIds);
      scItemMap = Object.fromEntries((scRows || []).map(r => [r.id, r]));
    }
    const stamped = (l || []).map(row => {
      const scLink = row.sc_item_id ? scItemMap[row.sc_item_id] : null;
      const withCatalog = scLink ? {
        _sc_source_url: scLink.source_url || null,
        _sc_group: scLink.item_group || null,
        _sc_category: scLink.category || null,
      } : {};
      if (row.pricing_mode === "merit_linked" && pegForCompute && Number.isFinite(Number(row.merit_price))) {
        return { ...row, ...withCatalog, auec_price: Math.round(Number(row.merit_price) * pegForCompute), _peggedAuec: true };
      }
      return { ...row, ...withCatalog };
    });
    setListings(stamped); setSections(s || []); setProfiles(p || []); setCartItems(c || []); setBids(b || []);
    setLoading(false);
  }

  // Load merit balance
  useEffect(() => {
    if (!user) return;
    async function loadBalance() {
      const { data } = await supabase.from("merit_balances").select("*").eq("user_id", user.id).single();
      if (data) setMeritBalance(data);
    }
    loadBalance();
  }, [user]);

  const tabListings = useMemo(() => {
    const filtered = listings.filter(l => {
      if (activeTab === "all") {
        // Exclude cash-only items by default
        if (!showCashItems && l.accepts_cash && !l.accepts_merits && !l.accepts_auec) return false;
      } else if (activeTab === "orgstore") {
        // Org Store = merit items + swag (both are org-managed storefronts)
        if (l.store_type !== "merit" && l.store_type !== "swag") return false;
      } else {
        if (l.store_type !== activeTab) return false;
      }
      if (search && !l.title.toLowerCase().includes(search.toLowerCase()) && !(l.description || "").toLowerCase().includes(search.toLowerCase())) return false;
      if (filterSection && l.section_id !== filterSection) return false;
      return true;
    });
    // Sort auctions to top in marketplace tab
    if (activeTab === "marketplace") {
      filtered.sort((a, b) => {
        const aIsAuction = a.is_auction && a.auction_end && new Date(a.auction_end) > new Date() ? 1 : 0;
        const bIsAuction = b.is_auction && b.auction_end && new Date(b.auction_end) > new Date() ? 1 : 0;
        return bIsAuction - aIsAuction;
      });
    }
    // Sort for Browse All tab
    if (activeTab === "all") {
      filtered.sort((a, b) => {
        // Auctions always on top
        const aIsAuction = a.is_auction && a.auction_end && new Date(a.auction_end) > new Date() ? 1 : 0;
        const bIsAuction = b.is_auction && b.auction_end && new Date(b.auction_end) > new Date() ? 1 : 0;
        if (bIsAuction !== aIsAuction) return bIsAuction - aIsAuction;
        if (browseSort === "merit_desc") return (b.merit_price || 0) - (a.merit_price || 0);
        if (browseSort === "merit_asc") return (a.merit_price || 0) - (b.merit_price || 0);
        if (browseSort === "auec_desc") return (Number(b.auec_price) || 0) - (Number(a.auec_price) || 0);
        if (browseSort === "auec_asc") return (Number(a.auec_price) || 0) - (Number(b.auec_price) || 0);
        return new Date(b.created_at) - new Date(a.created_at); // newest default
      });
    }
    return filtered;
  }, [listings, activeTab, search, filterSection, browseSort, showCashItems]);

  // Sections are universal — every section shows up on every market tab.
  // (store_type column on sections is retained for backwards compatibility
  // but no longer gates visibility.)
  const tabSections = useMemo(() => sections, [sections]);

  // Group listings by section (flat for Browse All)
  const groupedListings = useMemo(() => {
    if (activeTab === "all" || tabSections.length === 0) return [{ section: null, items: tabListings }];
    const groups = tabSections.map(s => ({ section: s, items: tabListings.filter(l => l.section_id === s.id) }));
    const uncategorized = tabListings.filter(l => !l.section_id);
    if (uncategorized.length > 0) groups.push({ section: null, items: uncategorized });
    return groups.filter(g => g.items.length > 0);
  }, [tabListings, tabSections]);

  // Cart functions
  async function addToCart(listingId, paymentType, quantity = 1) {
    if (!user) return;
    const existing = cartItems.find(c => c.listing_id === listingId && c.payment_type === paymentType);
    if (existing) {
      await supabase.from("store_cart_items").update({ quantity: existing.quantity + quantity }).eq("id", existing.id);
    } else {
      await supabase.from("store_cart_items").insert([{ user_id: user.id, listing_id: listingId, payment_type: paymentType, quantity }]);
    }
    const { data } = await supabase.from("store_cart_items").select("*").eq("user_id", user.id);
    setCartItems(data || []);
    setCartOpen(true);
  }

  async function removeCartItem(cartItemId) {
    await supabase.from("store_cart_items").delete().eq("id", cartItemId);
    setCartItems(prev => prev.filter(c => c.id !== cartItemId));
  }

  async function checkout() {
    if (!user || cartItems.length === 0) return;
    setCheckingOut(true);

    // Group items by seller for marketplace, or by store type for merit/swag
    const groupedBySeller = {};
    for (const ci of cartItems) {
      const listing = listings.find(l => l.id === ci.listing_id);
      if (!listing) continue;
      const key = listing.store_type === "marketplace" ? listing.listed_by : listing.store_type;
      if (!groupedBySeller[key]) groupedBySeller[key] = [];
      groupedBySeller[key].push({ cartItem: ci, listing });
    }

    // Create transactions for each group
    for (const [, group] of Object.entries(groupedBySeller)) {
      for (const { cartItem, listing } of group) {
        const tx = {
          store_type: listing.store_type,
          listing_id: listing.id,
          item_title: listing.title,
          item_description: listing.description || "",
          item_image_url: listing.image_url || "",
          quantity: cartItem.quantity,
          buyer_id: user.id,
          seller_id: listing.store_type === "marketplace" ? listing.listed_by : null,
          payment_type: cartItem.payment_type,
          merit_amount: cartItem.payment_type === "merits" ? (listing.merit_price || 0) * cartItem.quantity : null,
          auec_amount: cartItem.payment_type === "auec" ? (listing.auec_price || 0) * cartItem.quantity : null,
          cash_amount: cartItem.payment_type === "cash" ? (listing.cash_price || 0) * cartItem.quantity : null,
          status: "pending",
        };
        await supabase.from("store_transactions").insert([tx]);

        // Update listing quantity_sold if not permanent
        if (!listing.is_permanent && listing.quantity != null) {
          const newSold = listing.quantity_sold + cartItem.quantity;
          const updates = { quantity_sold: newSold };
          if (newSold >= listing.quantity) updates.status = "sold_out";
          await supabase.from("store_listings").update(updates).eq("id", listing.id);
        }
      }

      // Send Discord DM notification
      try {
        const items = group.map(g => g.listing);
        const buyer = profiles.find(p => p.id === user.id);
        await fetch("/api/store-notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "purchase",
            store_type: group[0].listing.store_type,
            seller_id: group[0].listing.store_type === "marketplace" ? group[0].listing.listed_by : null,
            buyer_name: buyer?.discord_username || "Unknown",
            buyer_id: user.id,
            items: group.map(g => ({
              title: g.listing.title,
              description: g.listing.description,
              image_url: g.listing.image_url,
              payment_type: g.cartItem.payment_type,
              amount: g.cartItem.payment_type === "merits" ? g.listing.merit_price : g.cartItem.payment_type === "auec" ? g.listing.auec_price : g.listing.cash_price,
              quantity: g.cartItem.quantity,
            })),
          }),
        });
      } catch (e) { console.error("Store notify error:", e); }
    }

    // Clear cart
    await supabase.from("store_cart_items").delete().eq("user_id", user.id);
    setCartItems([]);
    setCartOpen(false);
    setCheckingOut(false);
    loadData(); // Refresh listings

    // Refresh merit balance
    if (user) {
      const { data } = await supabase.from("merit_balances").select("*").eq("user_id", user.id).single();
      if (data) setMeritBalance(data);
    }
  }

  async function placeBid(listingId, amount, currency) {
    if (!user) return;
    // Insert bid record
    await supabase.from("store_bids").insert([{ listing_id: listingId, bidder_id: user.id, amount, currency }]);
    // Update listing with current bid
    await supabase.from("store_listings").update({ current_bid: amount, current_bidder_id: user.id, updated_at: new Date().toISOString() }).eq("id", listingId);
    // Refresh data
    loadData();
    // Refresh merit balance if bidding with merits
    if (currency === "merits") {
      const { data } = await supabase.from("merit_balances").select("*").eq("user_id", user.id).single();
      if (data) setMeritBalance(data);
    }
  }

  async function takeContract(listingId, qty) {
    if (!user || !profile) return { ok: false, error: "You must be signed in." };
    try {
      const r = await fetch("/api/store-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "take_contract", listing_id: listingId, seller_id: profile.id, quantity: qty || 1 }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: data.error || `Request failed (${r.status})` };
      // Refresh listings so quantity_sold / status update
      loadData();
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }

  async function shareListing(listingId) {
    if (!user || !profile) return { ok: false, error: "You must be signed in." };
    try {
      const r = await fetch("/api/store-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "share_listing", listing_id: listingId, shared_by: profile.id }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: data.error || `Request failed (${r.status})` };
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }

  function handleSelectListing(id) {
    setSelectedListing(id);
    navigate(`/market/${id}`);
  }

  function handleBackToGrid() {
    setSelectedListing(null);
    navigate("/market");
  }

  const detailListing = selectedListing ? listings.find(l => l.id === selectedListing) : null;

  const TABS = [
    { id: "marketplace", label: "Marketplace", icon: "⬡", color: "#4fc3f7" },
    { id: "bounty", label: "Bounties", icon: "◈", color: PURPLE },
    { id: "orgstore", label: "Org Store", icon: "◆", color: ACCENT },
    { id: "all", label: "Browse All", icon: "◎", color: ACCENT },
  ];

  if (loading) return (
    <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: `${ACCENT}66` }}>LOADING...</div>
  );

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::selection { background: ${ACCENT}; color: #05070f; } a { text-decoration: none; color: inherit; } .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; } .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: ${ACCENT}; transition: width 0.3s ease; } .nav-link:hover { color: ${ACCENT}; } .nav-link:hover::after { width: 100%; } .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); } input:focus, select:focus, textarea:focus { border-color: ${ACCENT} !important; outline: none; } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); } ::-webkit-scrollbar-thumb { background: rgba(252,214,87,0.3); }
        /* Listing card grid — wider min on desktop, collapses to ~2 cards across on mobile portrait. */
        .market-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
        @media (max-width: 700px) {
          .market-grid { gap: 8px; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
          .listing-card-content { padding: 8px 10px !important; }
          .listing-title { font-size: 11px !important; line-height: 1.25 !important; }
          .listing-desc { display: none !important; }
          .listing-meta { font-size: 8px !important; }
          .listing-badge { font-size: 7px !important; padding: 2px 5px !important; top: 5px !important; right: 5px !important; }
          .listing-badge-num { font-size: 9px !important; }
          .listing-badge-label-hide-mobile { display: none !important; }
          .listing-store-tag { font-size: 6px !important; padding: 1px 4px !important; }
          .listing-seller { font-size: 7px !important; }
          .listing-auction { padding: 4px 6px !important; gap: 4px !important; }
          .listing-auction-label { font-size: 6px !important; letter-spacing: 0.5px !important; }
        }`}</style>
      <div className="scanline" />
      <StarField />
      <Nav accentColor={ACCENT} />

      <div style={{ position: "relative", zIndex: 1, padding: "calc(96px + env(safe-area-inset-top, 0px)) clamp(16px, 4vw, 60px) 60px" }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <SectionLabel>Org Market</SectionLabel>
          <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 4vw, 48px)", color: "#e8eaf6", marginBottom: 8 }}>
            Market
          </h1>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 17, color: "rgba(232,234,246,0.45)" }}>
            Browse items, trade with org members, and spend your merits.
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 32, borderBottom: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearch(""); setFilterSection(""); setSelectedListing(null); navigate("/market"); }} style={{
              padding: "14px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
              background: activeTab === tab.id ? `${tab.color}10` : "transparent",
              borderBottom: activeTab === tab.id ? `2px solid ${tab.color}` : "2px solid transparent",
              border: "none", borderBottomStyle: "solid", borderBottomWidth: 2, borderBottomColor: activeTab === tab.id ? tab.color : "transparent",
              color: activeTab === tab.id ? tab.color : "rgba(232,234,246,0.4)", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 14 }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}

          {/* Merit balance + Cart */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            {meritBalance && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, color: "#fcd657", lineHeight: 1 }}>{meritBalance.available}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "rgba(252,214,87,0.5)", textTransform: "uppercase" }}>Merits</div>
                </div>
                {meritBalance.pending > 0 && (
                  <div style={{ textAlign: "right", opacity: 0.6 }}>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: "rgba(252,214,87,0.5)", lineHeight: 1 }}>{meritBalance.pending}</div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "rgba(252,214,87,0.3)", textTransform: "uppercase" }}>Pending</div>
                  </div>
                )}
              </div>
            )}
            <button onClick={() => setCartOpen(true)} style={{
              padding: "10px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
              background: cartItems.length > 0 ? `${ACCENT}15` : "transparent",
              border: `1px solid ${cartItems.length > 0 ? ACCENT : "rgba(255,255,255,0.12)"}`,
              color: cartItems.length > 0 ? ACCENT : "rgba(232,234,246,0.4)", cursor: "pointer",
            }}>
              CART ({cartItems.length})
            </button>
          </div>
        </div>

        {/* Detail view */}
        {detailListing ? (
          <ListingDetail listing={detailListing} onBack={handleBackToGrid} profiles={profiles} onAddToCart={addToCart} meritBalance={meritBalance} profile={profile} onPlaceBid={placeBid} bids={bids} onTakeContract={takeContract} onShareListing={shareListing} onEditListing={(l) => setEditingBounty(l)} />
        ) : (
          <>
            {/* Search & Filter bar */}
            <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
              <input type="text" placeholder="Search listings..." value={search} onChange={e => setSearch(e.target.value)} style={{
                flex: 1, minWidth: 200, padding: "10px 14px", background: "rgba(255,255,255,0.04)",
                border: `1px solid ${ACCENT}25`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2,
              }} />
              {tabSections.length > 0 && (
                <select value={filterSection} onChange={e => setFilterSection(e.target.value)} style={{
                  padding: "10px 14px", background: "#0a0610", border: `1px solid ${ACCENT}25`,
                  color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 160,
                }}>
                  <option value="">All Sections</option>
                  {tabSections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              {activeTab === "marketplace" && (
                <button onClick={() => setShowCreateListing(true)} style={{
                  padding: "10px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
                  background: "rgba(79,195,247,0.1)", border: "1px solid rgba(79,195,247,0.5)", color: "#4fc3f7", cursor: "pointer",
                }}>+ New Listing</button>
              )}
              {activeTab === "bounty" && (
                <button onClick={() => setShowCreateBounty(true)} style={{
                  padding: "10px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
                  background: `${PURPLE}18`, border: `1px solid ${PURPLE}88`, color: PURPLE, cursor: "pointer",
                }}>+ New Bounty</button>
              )}
              {activeTab === "all" && (
                <>
                  <select value={browseSort} onChange={e => setBrowseSort(e.target.value)} style={{
                    padding: "10px 14px", background: "#0a0610", border: `1px solid ${ACCENT}25`,
                    color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 180,
                  }}>
                    <option value="newest">Newest First</option>
                    <option value="merit_desc">Merits: High → Low</option>
                    <option value="merit_asc">Merits: Low → High</option>
                    <option value="auec_desc">aUEC: High → Low</option>
                    <option value="auec_asc">aUEC: Low → High</option>
                  </select>
                  <button onClick={() => setShowCashItems(v => !v)} style={{
                    padding: "10px 16px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
                    background: showCashItems ? "rgba(52,211,153,0.12)" : "transparent",
                    border: `1px solid ${showCashItems ? "rgba(52,211,153,0.5)" : "rgba(255,255,255,0.12)"}`,
                    color: showCashItems ? "#34d399" : "rgba(232,234,246,0.4)", cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap",
                  }}>{showCashItems ? "✓ Cash Items" : "$ Include Cash"}</button>
                </>
              )}
            </div>

            {/* Merit balance bar */}
            {meritBalance && activeTab === "orgstore" && (
              <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
                <div style={{ padding: "12px 20px", border: "1px solid rgba(252,214,87,0.2)", background: "rgba(252,214,87,0.04)", flex: "1 1 140px" }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 24, color: "#fcd657" }}>{meritBalance.available}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>Available</div>
                </div>
                <div style={{ padding: "12px 20px", border: "1px solid rgba(252,214,87,0.1)", background: "rgba(252,214,87,0.02)", flex: "1 1 140px" }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 24, color: "rgba(252,214,87,0.5)" }}>{meritBalance.pending}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.25)", textTransform: "uppercase" }}>Pending</div>
                </div>
                <div style={{ padding: "12px 20px", border: "1px solid rgba(252,214,87,0.1)", background: "rgba(252,214,87,0.02)", flex: "1 1 140px" }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 24, color: "rgba(252,214,87,0.3)" }}>{meritBalance.total_earned}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.25)", textTransform: "uppercase" }}>Total Earned</div>
                </div>
              </div>
            )}

            {/* Listings grid */}
            {tabListings.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80 }}>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 36, color: "rgba(255,255,255,0.05)", marginBottom: 12 }}>◇</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.2)" }}>
                  {search || filterSection ? "NO LISTINGS MATCH YOUR FILTERS" : "NO LISTINGS YET"}
                </div>
                {activeTab === "marketplace" && !search && (
                  <button onClick={() => setShowCreateListing(true)} style={{
                    marginTop: 20, padding: "10px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2,
                    background: "rgba(79,195,247,0.1)", border: "1px solid rgba(79,195,247,0.4)", color: "#4fc3f7", cursor: "pointer",
                  }}>BE THE FIRST — CREATE A LISTING</button>
                )}
                {activeTab === "bounty" && !search && (
                  <button onClick={() => setShowCreateBounty(true)} style={{
                    marginTop: 20, padding: "10px 24px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2,
                    background: `${PURPLE}18`, border: `1px solid ${PURPLE}66`, color: PURPLE, cursor: "pointer",
                  }}>POST THE FIRST BOUNTY</button>
                )}
              </div>
            ) : (
              groupedListings.map((group, gi) => (
                <div key={gi} style={{ marginBottom: 40 }}>
                  {group.section && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: "#e8eaf6", letterSpacing: 1 }}>{group.section.name}</div>
                      {group.section.description && <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.35)", marginTop: 2 }}>{group.section.description}</div>}
                    </div>
                  )}
                  <div className="market-grid">
                    {group.items.map(listing => {
                      const sellerProfile = profiles.find(p => p.id === listing.listed_by);
                      return (
                        <ListingCard key={listing.id} listing={listing} onClick={() => handleSelectListing(listing.id)} sellerName={((activeTab === "marketplace" || activeTab === "bounty" || activeTab === "all") && (listing.store_type === "marketplace" || listing.store_type === "bounty")) ? sellerProfile?.discord_username : null} storeTag={(activeTab === "all" || activeTab === "orgstore") ? listing.store_type : null} />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* Cart sidebar */}
      {cartOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050 }} onClick={() => setCartOpen(false)} />
          <CartSidebar cartItems={cartItems} listings={listings} profiles={profiles} onClose={() => setCartOpen(false)} onRemoveItem={removeCartItem} onCheckout={checkout} checkingOut={checkingOut} meritBalance={meritBalance} />
        </>
      )}

      {/* Create listing modal */}
      {showCreateListing && (
        <CreateListingModal onClose={() => setShowCreateListing(false)} onCreated={loadData} profile={profile} sections={sections} meritPegAuec={meritPegAuec} />
      )}

      {/* Create bounty modal */}
      {showCreateBounty && (
        <CreateBountyModal onClose={() => setShowCreateBounty(false)} onCreated={loadData} profile={profile} sections={sections} />
      )}

      {/* Edit bounty modal — same form, prefilled */}
      {editingBounty && (
        <CreateBountyModal
          existing={editingBounty}
          onClose={() => setEditingBounty(null)}
          onCreated={() => { loadData(); setEditingBounty(null); }}
          sections={sections}
        />
      )}
    </div>
  );
}
